import { cn } from "@/lib/utils";

/**
 * One horizontal bar. `color` is a full CSS color value (typically a design
 * token, e.g. `"var(--success)"`) so the bar stays theme-aware. Defaults to
 * the first categorical series token.
 */
export interface BarItem {
  label: string;
  value: number;
  color?: string;
  /** Unique key for `onSelect`. Defaults to `label` when omitted. */
  key?: string;
  /**
   * Replaces the bar and value with this text — for a row that has a value
   * but shouldn't be plotted as one (e.g. a population too small to be
   * meaningful, which would otherwise read as a misleadingly low bar).
   */
  note?: string;
}

interface BarListProps {
  items: BarItem[];
  /** Accessible caption for the list (visually hidden). */
  title: string;
  /**
   * Scale ceiling. Bars fill relative to this. Defaults to the largest value,
   * so a single dominant category doesn't dwarf the rest to invisibility.
   */
  max?: number;
  formatValue?: (n: number) => string;
  className?: string;
  /** Row becomes a button when present; not called for a `note` row. */
  onSelect?: (item: BarItem) => void;
}

/**
 * A hand-built horizontal bar list — the ranked-magnitude primitive. Each row
 * is `label · track(fill) · value`; the value rides at the tip so nothing is
 * gated behind a tooltip. Generic (data in, SVG-free markup out).
 *
 * Accessibility: rendered as a real `<dl>` with visible numeric values, so the
 * markup is itself the data table; the fill is decorative (`aria-hidden`) and
 * carries a 4px rounded data-end anchored to the baseline (marks spec). Labels
 * and values wear text tokens, never the bar color.
 */
export function BarList({
  items,
  title,
  max,
  formatValue = (n) => n.toLocaleString(),
  className,
  onSelect,
}: BarListProps) {
  const ceiling = Math.max(max ?? 0, ...items.filter((i) => !i.note).map((i) => i.value), 1);

  return (
    <dl className={cn("space-y-2.5", className)}>
      <span className="sr-only">{title}</span>
      {items.map((item) => {
        const pct = Math.max(0, Math.min(100, (item.value / ceiling) * 100));
        const row = (
          <>
            <dt className="min-w-0 truncate text-sm text-muted-foreground">{item.label}</dt>
            <dd className="text-sm font-medium tabular-nums text-foreground">
              {item.note ?? formatValue(item.value)}
            </dd>
            {!item.note && (
              <div
                aria-hidden="true"
                className="col-span-2 h-2 overflow-hidden rounded-full bg-muted"
              >
                <div
                  className="h-full rounded-full transition-[width] duration-300"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: item.color ?? "var(--chart-1)",
                  }}
                />
              </div>
            )}
          </>
        );
        const gridClass = item.note
          ? "grid grid-cols-[1fr_auto] items-center gap-x-3"
          : "grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1";
        return onSelect && !item.note ? (
          <button
            key={item.key ?? item.label}
            type="button"
            onClick={() => onSelect(item)}
            className={cn(gridClass, "w-full rounded-md text-left hover:bg-muted/40")}
          >
            {row}
          </button>
        ) : (
          <div key={item.key ?? item.label} className={gridClass}>
            {row}
          </div>
        );
      })}
    </dl>
  );
}
